import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { logAnalyticsEvent } from '../utils/analytics';
import { useFooter } from '../contexts/FooterContext';
import LandingHero from './landing/LandingHero';
import TrustBar from './landing/TrustBar';
import StandardSection from './landing/StandardSection';
import HowItWorks from './landing/HowItWorks';
import ExploreGrid from './landing/ExploreGrid';
import Membership from './landing/Membership';
import AppDownload from './landing/AppDownload';
import LandingFooter from './landing/LandingFooter';
import { LANDING_FONT, LC } from './landing/landingTokens';

// ─── Landing page (green + Archivo redesign) ─────────────────────────────────
// Faithful to Brand_Design/mystack-landing.html. Tokens are landing-scoped
// (see components/landing/landingTokens.js); the global theme is untouched.
// The global Footer is hidden here in favor of the landing-only footer.
// See docs/landing-page-redesign/IMPLEMENTATION.md.

const homepageDescription =
  'myStack reads 10,000+ human clinical studies to build supplement stacks matched to your health profile, with interaction and dose checks.';

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'myStack',
  url: 'https://my-stack.ai/',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://my-stack.ai/supplements/{search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'myStack',
  url: 'https://my-stack.ai/',
  logo: 'https://my-stack.ai/logo512.png',
  description: homepageDescription,
  sameAs: ['https://x.com/mystackai'],
};

export default function LandingPage({ appPlatform }) {
  const navigate = useNavigate();
  const { setShowFooter } = useFooter();
  const [healthGoals, setHealthGoals] = useState('');

  // Landing renders its own footer; hide the global one while mounted.
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  // Preserve the live hero funnel: stash goal + flag, fire analytics, route to
  // the stack generator. (Wiring unchanged from the prior landing.)
  const handleGetStarted = () => {
    if (!healthGoals.trim()) return;
    const goals = healthGoals.trim();
    sessionStorage.setItem('pendingHealthGoals', goals);
    sessionStorage.setItem('shouldGenerateStack', 'true');
    logAnalyticsEvent('click_recommend_stack_landing', { health_goals: goals });
    navigate('/create-stack?source=landing');
  };

  return (
    <Box sx={{ fontFamily: LANDING_FONT, background: LC.surface, color: LC.ink }}>
      <Helmet>
        <title>myStack: Health Intelligence for Supplements</title>
        <meta
          name="description"
          content={homepageDescription}
        />
        <link rel="canonical" href="https://my-stack.ai/" />
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <LandingHero
        healthGoals={healthGoals}
        setHealthGoals={setHealthGoals}
        onSubmit={handleGetStarted}
      />
      <TrustBar />
      <StandardSection />
      <HowItWorks />
      <ExploreGrid />
      <Membership />
      {/* App-store CTAs only make sense on web — someone already inside the
          native app webview doesn't need to be told to download it. */}
      {appPlatform === 'web' && <AppDownload />}
      <LandingFooter />
    </Box>
  );
}
